import React, { createContext, useContext, useState } from 'react'
import Alert from '../components/Alert'

const AlertContext = createContext()

export const useAlert = () => {
  const context = useContext(AlertContext)
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider')
  }
  return context
}

export const AlertProvider = ({ children }) => {
  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    cancelText: 'Cancel',
    showCancel: false,
    onConfirm: null,
    onCancel: null
  })

  const showAlert = ({
    title = 'Alert',
    message = '',
    type = 'info',
    confirmText = 'OK',
    cancelText = 'Cancel',
    showCancel = false,
    onConfirm = null,
    onCancel = null
  }) => {
    setAlertState({
      isOpen: true,
      title,
      message,
      type,
      confirmText,
      cancelText,
      showCancel,
      onConfirm,
      onCancel
    })
  }

  const hideAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }))
  }

  // Convenience methods for different alert types
  const alert = {
    info: (message, title = 'Information') => 
      showAlert({ title, message, type: 'info' }),
    
    success: (message, title = 'Success') => 
      showAlert({ title, message, type: 'success' }),
    
    warning: (message, title = 'Warning') => 
      showAlert({ title, message, type: 'warning' }),
    
    error: (message, title = 'Error') => 
      showAlert({ title, message, type: 'error' }),
    
    confirm: (message, title = 'Confirm', onConfirm, onCancel) => 
      showAlert({ 
        title, 
        message, 
        type: 'warning', 
        showCancel: true, 
        confirmText: 'Yes',
        cancelText: 'No',
        onConfirm, 
        onCancel 
      })
  }

  const value = {
    ...alertState,
    showAlert,
    hideAlert,
    alert
  }

  return (
    <AlertContext.Provider value={value}>
      {children}
      <Alert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        onCancel={alertState.onCancel}
      />
    </AlertContext.Provider>
  )
}
